import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import { FaSchool, FaUsers, FaChalkboardTeacher, FaPercent, FaArrowRight, FaCheckCircle } from 'react-icons/fa';
import Navbar from '../../components/Navbar';
import Footer from '../../components/footer';
import Scrollbar from '../../components/scrollbar';
import Logo from '../../images/logo.svg';
import { useTranslation } from 'react-i18next';
import './style.css';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import { useMetaPixel } from '../../contexts/MetaPixelContext';

const BecomePartnerPage = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { trackPartnerApplicationStarted, trackPartnerApplicationSubmitted } = useAnalytics();
  const { trackLead } = useMetaPixel();

  // Track start of application on mount
  React.useEffect(() => {
    trackPartnerApplicationStarted();
  }, [trackPartnerApplicationStarted]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.firstName || !formData.lastName || !formData.email || !formData.message) {
      toast.error(t('becomePartner.fillAllFields'));
      return;
    }

    setIsSubmitting(true);

    try {
      const baseUrl = process.env.NODE_ENV === 'production' ? '/api' : (process.env.REACT_APP_API_URL || 'http://localhost:8000');
      const payload = {
        ...formData,
        userType: 'school',
      };

      const response = await fetch(`${baseUrl}/api/v1/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Failed to submit request');
      }

      setIsSubmitted(true);

      // Track successful submission
      trackPartnerApplicationSubmitted({
        email: formData.email,
        phone: formData.phone,
        firstName: formData.firstName,
        lastName: formData.lastName
      });

      // Track Lead in Meta Pixel
      trackLead({ contentName: 'partner_application', email: formData.email });

      toast.success(t('becomePartner.successTitle'));
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        message: '',
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error submitting partner form:', error);
      toast.error(t('becomePartner.submitError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderSuccessState = () => (
    <div className="partner-page">
      <Navbar Logo={Logo} hclass="wpo-header-style-1" />
      <div className="partner-hero partner-hero-success">
        <div className="partner-hero-inner container">
          <div className="partner-success-card">
            <div className="partner-success-icon">
              <FaCheckCircle />
            </div>
            <h1>{t('becomePartner.successTitle')}</h1>
            <p>
              {t('becomePartner.successMessage')}
            </p>
            <button
              type="button"
              className="partner-secondary-btn"
              onClick={() => setIsSubmitted(false)}
            >
              {t('becomePartner.sendAnother')}
            </button>
          </div>
        </div>
      </div>
      <Footer />
      <Scrollbar />
    </div>
  );

  if (isSubmitted) {
    return renderSuccessState();
  }

  return (
    <div className="partner-page">
      <Helmet>
        <title>{t('becomePartner.title')}</title>
        <meta
          name="description"
          content={t('becomePartner.metaDescription')}
        />
      </Helmet>

      <Navbar Logo={Logo} hclass="wpo-header-style-1" />

      <section className="partner-hero">
        <div className="partner-hero-overlay" />
        <div className="partner-hero-inner container">
          <div className="partner-hero-text">
            <span className="partner-label">{t('becomePartner.label')}</span>
            <h1>{t('becomePartner.heroTitle')}</h1>
            <h2>{t('becomePartner.heroSubtitle')}</h2>

            <div className="partner-hero-badges">
              <div className="partner-badge">
                <FaSchool />
                <span>{t('becomePartner.badgeSchool')}</span>
              </div>
              <div className="partner-badge">
                <FaUsers />
                <span>{t('becomePartner.badgeUsers')}</span>
              </div>
              <div className="partner-badge">
                <FaPercent />
                <span>{t('becomePartner.badgeDiscount')}</span>
              </div>
            </div>

            <p className="partner-hero-note">
              {t('becomePartner.heroNote')}
            </p>
          </div>
        </div>
      </section>

      <section className="partner-content-section">
        <div className="container partner-content-grid">
          <div className="partner-benefits-card">
            <h3>{t('becomePartner.whyPartner')}</h3>
            <ul className="partner-benefits-list">
              <li>
                <FaCheckCircle className="partner-benefit-icon" />
                <span>{t('becomePartner.benefit1')}</span>
              </li>
              <li>
                <FaCheckCircle className="partner-benefit-icon" />
                <span>{t('becomePartner.benefit2')}</span>
              </li>
              <li>
                <FaCheckCircle className="partner-benefit-icon" />
                <span>{t('becomePartner.benefit3')}</span>
              </li>
              <li>
                <FaCheckCircle className="partner-benefit-icon" />
                <span>{t('becomePartner.benefit4')}</span>
              </li>
            </ul>

            <div className="partner-teacher-highlight">
              <FaChalkboardTeacher className="partner-teacher-icon" />
              <div>
                <h4>{t('becomePartner.builtForTeachers')}</h4>
                <p>
                  {t('becomePartner.builtForTeachersDesc')}
                </p>
              </div>
            </div>
          </div>

          <div className="partner-form-card">
            <h3>{t('becomePartner.requestAccess')}</h3>
            <p>
              {t('becomePartner.requestAccessDesc')}
            </p>

            <form className="partner-form" onSubmit={handleSubmit}>
              <div className="partner-form-row">
                <div className="partner-form-group">
                  <label htmlFor="firstName">
                    {t('becomePartner.firstName')} <span className="required">*</span>
                  </label>
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder={t('becomePartner.firstNamePlaceholder')}
                    required
                  />
                </div>
                <div className="partner-form-group">
                  <label htmlFor="lastName">
                    {t('becomePartner.lastName')} <span className="required">*</span>
                  </label>
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder={t('becomePartner.lastNamePlaceholder')}
                    required
                  />
                </div>
              </div>

              <div className="partner-form-row">
                <div className="partner-form-group">
                  <label htmlFor="email">
                    {t('becomePartner.workEmail')} <span className="required">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder={t('becomePartner.workEmailPlaceholder')}
                    required
                  />
                </div>
                <div className="partner-form-group">
                  <label htmlFor="phone">{t('becomePartner.phone')}</label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder={t('becomePartner.phonePlaceholder')}
                  />
                </div>
              </div>

              <div className="partner-form-group">
                <label htmlFor="message">
                  {t('becomePartner.tellUs')} <span className="required">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder={t('becomePartner.tellUsPlaceholder')}
                  required
                />
              </div>

              <button
                type="submit"
                className="partner-primary-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  t('becomePartner.sending')
                ) : (
                  <>
                    {t('becomePartner.requestCall')} <FaArrowRight />
                  </>
                )}
              </button>

              <p className="partner-small-print">
                {t('becomePartner.smallPrint')}
              </p>
            </form>
          </div>
        </div>
      </section>

      <Footer />
      <Scrollbar />
    </div>
  );
};

export default BecomePartnerPage;
